import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { usePatientsByDischargeDate } from "@/hooks/usePatients";
import { TentativeDischargeTableRow } from "./TentativeDischargeTableRow";
import { PatientDetailDrawer } from "./PatientDetailDrawer";
import { Patient } from "./PatientCard";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

type FilterOption = "today" | "tomorrow" | "later";

const getDateRange = (filter: FilterOption) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  
  const farFuture = new Date(today);
  farFuture.setFullYear(farFuture.getFullYear() + 1);
  
  switch(filter) {
    case "today":
      return { start: today, end: today };
    case "tomorrow":
      return { start: tomorrow, end: tomorrow };
    case "later":
      return { start: dayAfterTomorrow, end: farFuture };
  }
};

const getFilterLabel = (filter: FilterOption) => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  switch(filter) {
    case "today":
      return `Today (${format(today, "MMM dd")})`;
    case "tomorrow":
      return `Tomorrow (${format(tomorrow, "MMM dd")})`;
    case "later":
      return `Later (after ${format(tomorrow, "MMM dd")})`;
  }
};

export const TentativeDischargeView = () => {
  const [activeFilter, setActiveFilter] = useState<FilterOption>("today");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const dateRange = getDateRange(activeFilter);
  const { data: patients, isLoading, error } = usePatientsByDischargeDate(dateRange.start, dateRange.end);

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsDrawerOpen(true);
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6">
      {/* Header Section with Filters */}
      <div className="mb-6 space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Tentative Discharge</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View patients scheduled for discharge
          </p>
        </div>
        
        {/* Filter Toggle Group */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <ToggleGroup 
            type="single" 
            value={activeFilter} 
            onValueChange={(value) => value && setActiveFilter(value as FilterOption)}
            className="justify-start"
          >
            <ToggleGroupItem value="today" aria-label="Today">
              Today
            </ToggleGroupItem>
            <ToggleGroupItem value="tomorrow" aria-label="Tomorrow">
              Tomorrow
            </ToggleGroupItem>
            <ToggleGroupItem value="later" aria-label="Later">
              Later
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {/* Selected Filter Display & Patient Count */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm text-muted-foreground">
            Showing patients for:{" "}
            <span className="font-medium text-foreground">
              {getFilterLabel(activeFilter)}
            </span>
          </div>
          {!isLoading && patients && (
            <Badge variant="secondary" className="text-sm">
              {patients.length} {patients.length === 1 ? "patient" : "patients"} expected
            </Badge>
          )}
        </div>
      </div>

      {/* Patient Grid */}
      <div className="mt-6">
        {isLoading && (
          <div className="border rounded-lg bg-card overflow-hidden">
            <div className="bg-muted/30 p-3 flex gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-28" />
              ))}
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-4 p-3 border-t border-border">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-12">
            <div className="text-destructive">Error loading patients. Please try again.</div>
          </div>
        )}

        {!isLoading && !error && patients && patients.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No patients scheduled
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              There are no patients expected to be discharged for the selected filter
            </p>
          </div>
        )}

        {!isLoading && !error && patients && patients.length > 0 && (
          <div className="border rounded-lg bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[200px]">Patient Info</TableHead>
                  <TableHead className="w-[180px]">Ward / Bed</TableHead>
                  <TableHead className="w-[150px]">Doctor</TableHead>
                  <TableHead className="w-[150px]">Expected Discharge</TableHead>
                  <TableHead className="w-[180px]">TAT Status</TableHead>
                  <TableHead className="w-[150px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patients.map((patient) => (
                  <TentativeDischargeTableRow
                    key={patient.id}
                    patient={patient}
                    onClick={() => handlePatientClick(patient)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Patient Detail Drawer */}
      {selectedPatient && (
        <PatientDetailDrawer
          patient={selectedPatient}
          open={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
        />
      )}
    </div>
  );
};
